// @flow
import { connect } from 'react-redux'
import AdEdit, { type Props, getImageSizeRestrictionsFromSingleConfig } from './AdEdit'
import * as actions from '../redux/ducks/advertisements/reducers'
import { getAdvertisement, hasAdvertisementAnyErrors } from '../redux/selectors'
import type { Store } from '../redux/ducks'
import * as replicaActions from '../redux/ducks/advertisementReplicas/reducers'
import store from '../redux/store'
import type {
  AudienceData,
  Configs,
  Crop,
  UrlCheckResult,
  ValidatedAdvertisementWithStoredMedia,
  ValidatedAdvertisementWithStoredMediaPatch,
  OptionType,
  ImageConfigCollection,
  ProviderImageConfigs
} from './types'
import _ from 'lodash'
import { Audience } from './constants'
import type {
  AdvertisementReplica,
  AdvertisementReplicaPatch
} from '../redux/ducks/advertisementReplicas/reducers'
import type { ControlVisibility } from './AdEdit'
import { adProviderTemplateNames } from './prepareProviderTemplateBlocks'
import {
  calculateColumnImageSizeRestrictions,
  calculateMultiImageSizeRestrictions
} from './AdvertisementTemplatePage/EditForm/ReplicatedDataContainer'
import { controlVisibilityFromList } from './connectProviderTemplatesWithControls'
import type { PlacementType } from './AudienceEdit/PlacementTypeSelect'

type OwnProps = {|
  configs: Configs,
  audienceData: AudienceData,
  storeId: string,
  maxMediaCount: number,
  onChange: (diff: Object) => void,
  isPromotionUrlEditDisabled?: boolean,
  urlCheck?: ?UrlCheckResult,
  compact?: boolean,
  action: 'copy' | 'update' | 'create',
  fbPromotionPosts: Array<OptionType>
|}

function mapStateToProps(state: Store, ownProps: OwnProps) {
  const {
    storeId,
    configs,
    audienceData,
    maxMediaCount,
    isPromotionUrlEditDisabled,
    urlCheck,
    compact,
    action,
    fbPromotionPosts
  } = ownProps
  const ad: ValidatedAdvertisementWithStoredMedia = getAdvertisement(state, storeId)
  const replicas = state.advertisementReplicas
  const fbPlacements = audienceData.fbPlacements

  const providerTemplateNames = adProviderTemplateNames(
    audienceData.providers,
    audienceData.audienceType,
    audienceData.placementType,
    audienceData.vkPlacement
  )

  const controlVisibility: ControlVisibility = controlVisibilityFromList(providerTemplateNames)

  controlVisibility.fbInstagramImage =
    controlVisibility.fbInstagramImage && _.includes(fbPlacements.instagram, 'stream')
  controlVisibility.fbStoriesImage =
    controlVisibility.fbStoriesImage &&
    (_.includes(fbPlacements.instagram, 'story') || _.includes(fbPlacements.facebook, 'story'))

  const imageConfigs = imageConfigsFromCollection(
    configs.imageConfigCollection,
    audienceData.placementType,
    audienceData.vkPlacement
  )

  const errors = {
    vkUrlErrors: ad.vkUrlErrors,
    tmUrlErrors: ad.tmUrlErrors,
    fbUrlErrors: ad.fbUrlErrors,
    vkTitleErrors: ad.vkTitleErrors,
    fbTitleErrors: ad.fbTitleErrors,
    urlErrors: ad.urlErrors,
    titleErrors: ad.titleErrors,
    vkLinkTitleErrors: ad.vkLinkTitleErrors,
    vkBigImageErrors: ad.vkBigImageErrors,
    tmSmallImageErrors: ad.tmSmallImageErrors,
    multiImageErrors: ad.multiImageErrors,
    fbInstagramImageErrors: ad.fbInstagramImageErrors,
    fbStoriesImageErrors: ad.fbStoriesImageErrors,
    fbThumbnailImageErrors: ad.fbThumbnailImageErrors,
    videoErrors: ad.videoErrors,
    logoImageErrors: ad.logoImageErrors,
    snippetImageErrors: ad.snippetImageErrors,
    productTitleErrors: ad.productTitleErrors,
    productDescriptionErrors: ad.productDescriptionErrors,
    columnDescriptionErrors: ad.columnDescriptionErrors,
    multiDescriptionErrors: ad.multiDescriptionErrors,
    vkMultiDescriptionErrors: ad.vkMultiDescriptionErrors,
    vkWithoutButtonDescriptionErrors: ad.vkWithoutButtonDescriptionErrors,
    fbMultiDescriptionErrors: ad.fbMultiDescriptionErrors,
    columnImageErrors: ad.columnImageErrors,
    tmCarouselDescriptionErrors: ad.tmCarouselDescriptionErrors
  }

  const hasAnyErrors = hasAdvertisementAnyErrors(state, storeId)

  const hasAnyMedias =
    audienceData.audienceType !== Audience.types.product_retargeting &&
    audienceData.audienceType !== Audience.types.posts_promotion

  return {
    ...errors,
    controlVisibility,
    action,
    configs,
    maxMediaCount,
    isPromotionUrlEditDisabled: isPromotionUrlEditDisabled || false,
    urlCheck,
    compact: !!compact,
    replicas,
    vkUrl: ad.vkUrl || '',
    tmUrl: ad.tmUrl || '',
    fbUrl: ad.fbUrl || '',
    fbPromotionPosts,
    productTitle: ad.productTitle || '',
    productDescription: ad.productDescription || '',
    vkTitle: ad.vkTitle || '',
    fbTitle: ad.fbTitle || '',
    advertisementTemplateId: ad.advertisementTemplateId,
    url: ad.url,
    title: ad.title,
    vkLinkTitle: ad.vkLinkTitle || '',
    vkLinkButton: ad.vkLinkButton || '',
    tmLinkButton: ad.tmLinkButton || '',
    fbLinkButton: ad.fbLinkButton || '',
    vkOptionsWithoutPublic:
      audienceData.vkPlacement === Audience.vk_placements.promopost_without_public,
    compactUrl: ownProps.compact || false,
    vkBigImageStoreId: ad.vkBigImageStoreId,
    tmSmallImageCrop: ad.tmSmallImageCrop,
    tmSmallImageStoreId: ad.tmSmallImageStoreId,
    fbInstagramImageStoreId: ad.fbInstagramImageStoreId,
    fbStoriesImageStoreId: ad.fbStoriesImageStoreId,
    fbThumbnailImageStoreId: ad.fbThumbnailImageStoreId,
    fbImageCrop: ad.fbImageCrop,
    fbThumbnailImageCrop: ad.fbThumbnailImageCrop,
    fbInstagramImageCrop: ad.fbInstagramImageCrop,
    fbStoriesImageCrop: ad.fbStoriesImageCrop,
    glImageCrop: ad.glImageCrop,
    videoStoreId: ad.videoStoreId,
    logoImageStoreId: ad.logoImageStoreId,
    logoImageCrop: ad.logoImageCrop,
    snippetImageStoreId: ad.snippetImageStoreId,
    snippetImageCrop: ad.snippetImageCrop,
    multiImageStoreId: ad.multiImageStoreId,
    imageConfigs,
    hasAnyErrors: hasAnyErrors,
    hasAnyMedias: hasAnyMedias,
    hasVideo: hasAnyMedias && audienceData.placementType === Audience.placement_types.with_video,
    columnImageSizeRestrictions: calculateColumnImageSizeRestrictions(controlVisibility, configs),
    multiImageSizeRestrictions: calculateMultiImageSizeRestrictions(controlVisibility, configs),
    vkBigImageSizeRestrictions: getImageSizeRestrictionsFromSingleConfig(configs.vkBigImageConfigs),
    vkMultiImageConfig: imageConfigs.vk,
    tmMultiImageConfig: imageConfigs.target_mail,
    fbMultiImageConfig: imageConfigs.facebook,
    glMultiImageConfig: imageConfigs.google,
    carouselStoreId: ad.carouselStoreId,
    columnDescription: ad.columnDescription,
    multiDescription: ad.multiDescription,
    vkMultiDescription: ad.vkMultiDescription,
    vkWithoutButtonDescription: ad.vkWithoutButtonDescription,
    fbMultiDescription: ad.fbMultiDescription,
    tmColumnCrop: ad.tmColumnCrop,
    tmMultiCrop: ad.tmMultiCrop,
    vkArticleCrop: ad.vkArticleCrop,
    vkColumnCrop: ad.vkColumnCrop,
    vkMultiCrop: ad.vkMultiCrop,
    vkSiteCrop: ad.vkSiteCrop,
    columnImageStoreId: ad.columnImageStoreId,
    tmCarouselDescription: ad.tmCarouselDescription
  }
}

function imageConfigsFromCollection(
  collection: ImageConfigCollection,
  placementType: PlacementType,
  vkPlacement: string
): ProviderImageConfigs {
  let config =
    placementType === Audience.placement_types.column ? collection.regular : collection.promo
  if (vkPlacement === Audience.vk_placements.promopost_without_public)
    config.vk = collection.site.vk
  return config
}

function getCurrentProps(ownProps: OwnProps) {
  return mapStateToProps(store.getState(), ownProps)
}

function mapDispatchToProps(dispatch: Function, ownProps: OwnProps) {
  const { onChange, storeId } = ownProps
  const change = (diff: ValidatedAdvertisementWithStoredMediaPatch) => {
    dispatch(actions.patch(storeId, diff))
    onChange && onChange(diff)
  }
  const addReplicaFunc = (imageStoreId?: ?string, vkBigImageStoreId?: ?string) => {
    const props = getCurrentProps(ownProps)

    dispatch(
      replicaActions.add({
        columnImageErrors: [],
        columnImageStoreId: imageStoreId || props.columnImageStoreId,
        multiImageStoreId: imageStoreId || props.multiImageStoreId,
        multiImageErrors: [],
        snippetImageStoreId: props.snippetImageStoreId,
        snippetImageCrop: props.snippetImageCrop,
        snippetImageErrors: props.snippetImageErrors,
        videoStoreId: null,
        videoErrors: [],
        tmColumnImageCrop: null,
        tmMultiImageCrop: null,
        tmSmallImageStoreId: null,
        tmSmallImageCrop: null,
        tmSmallImageErrors: [],
        fbImageCrop: null,
        fbInstagramImageStoreId: null,
        fbInstagramImageCrop: null,
        fbInstagramImageErrors: [],
        fbStoriesImageStoreId: null,
        fbStoriesImageCrop: null,
        fbStoriesImageErrors: [],
        fbThumbnailImageStoreId: null,
        fbThumbnailImageCrop: null,
        fbThumbnailImageErrors: [],
        vkColumnImageCrop: null,
        vkMultiImageCrop: null,
        vkArticleImageCrop: null,
        vkSiteImageCrop: null,
        vkBigImageStoreId: vkBigImageStoreId || props.vkBigImageStoreId,
        vkBigImageErrors: [],
        glImageCrop: null
      })
    )
  }
  const changeReplica = (index, diff: AdvertisementReplicaPatch) =>
    dispatch(replicaActions.patch(index, diff))
  return {
    onReplicaAdd: () => addReplicaFunc(null, null),
    onMediaRemove: () => dispatch(actions.mediaRemove(storeId)),
    onUrlChange: (val: string) => {
      change({ url: val, urlErrors: [], vkUrlErrors: [], fbUrlErrors: [] })
    },
    onTitleChange: (val: string) => change({ title: val, titleErrors: [] }),
    onColumnDescriptionChange: (val: string) =>
      change({ columnDescription: val, columnDescriptionErrors: [] }),
    onMultiDescriptionChange: (val: string) =>
      change({ multiDescription: val, multiDescriptionErrors: [] }),
    onVideoChange: (val: string) => change({ videoStoreId: val, videoErrors: [] }),
    onLogoImageChange: (val: string) => {
      change({ logoImageStoreId: val, logoImageErrors: [], logoImageCrop: null })
    },
    onLogoImageCropChange: (val: ?Crop) => {
      change({ logoImageCrop: val })
    },
    onProductDescriptionChange: (val: string) =>
      change({ productDescription: val, productDescriptionErrors: [] }),
    onProductTitleChange: (val: string) => change({ productTitle: val, productTitleErrors: [] }),
    onSnippetImageChange: (val: string, replicas: Array<AdvertisementReplica>) => {
      change({ snippetImageStoreId: val, snippetImageErrors: [], snippetImageCrop: null })
      _.each(replicas, (replica, index) => {
        changeReplica(index, {
          snippetImageStoreId: storeId,
          snippetImageErrors: [],
          snippetImageCrop: null
        })
      })
    },
    onSnippetImageCropChange: (val: Crop, replicas: Array<AdvertisementReplica>) => {
      change({ snippetImageCrop: val })
      _.each(replicas, (replica, index) => {
        changeReplica(index, { snippetImageCrop: val })
      })
    },
    onVkColumnCropChange: (val: Crop) => change({ vkColumnCrop: val }),
    onVkMultiCropChange: (val: Crop) => change({ vkMultiCrop: val }),
    onVkArticleCropChange: (val: Crop) => change({ vkArticleCrop: val }),
    onVkSiteCropChange: (val: Crop) => change({ vkSiteCrop: val }),
    onVkTitleChange: (val: string) => change({ vkTitle: val, vkTitleErrors: [] }),
    onVkMultiDescriptionChange: (val: string) =>
      change({ vkMultiDescription: val, vkMultiDescriptionErrors: [] }),
    onVkWithoutButtonDescriptionChange: (val: string) =>
      change({ vkWithoutButtonDescription: val, vkWithoutButtonDescriptionErrors: [] }),
    onVkUrlChange: (val: string) => change({ vkUrl: val, vkUrlErrors: [] }),
    onVkLinkButtonChange: (val: string) => change({ vkLinkButton: val }),
    onVkLinkTitleChange: (val: string) => change({ vkLinkTitle: val }),
    onTmColumnCropChange: (val: Crop) => change({ tmColumnCrop: val }),
    onTmMultiCropChange: (val: Crop) => change({ tmMultiCrop: val }),
    onTmLinkButtonChange: (val: string) => change({ tmLinkButton: val }),
    onTmSmallImageCropChange: (val: Crop) => change({ tmSmallImageCrop: val }),
    onTmSmallImageChange: (val: string) =>
      change({ tmSmallImageStoreId: val, tmSmallImageErrors: [], tmSmallImageCrop: null }),
    onTmUrlChange: (val: string) => change({ tmUrl: val, tmUrlErrors: [] }),
    onFbThumbnailImageChange: (val: string) =>
      change({
        fbThumbnailImageStoreId: val,
        fbThumbnailImageErrors: [],
        fbThumbnailImageCrop: null
      }),
    onFbThumbnailImageCropChange: (val: Crop) => change({ fbThumbnailImageCrop: val }),
    onFbInstagramImageCropChange: (val: Crop) => change({ fbInstagramImageCrop: val }),
    onFbStoriesImageCropChange: (val: Crop) => change({ fbStoriesImageCrop: val }),
    onFbImageCropChange: (val: Crop) => change({ fbImageCrop: val }),
    onFbTitleChange: (val: string) => change({ fbTitle: val, fbTitleErrors: [] }),
    onFbMultiDescriptionChange: (val: string) =>
      change({ fbMultiDescription: val, fbMultiDescriptionErrors: [] }),
    onFbUrlChange: (val: string) => change({ fbUrl: val, fbUrlErrors: [] }),
    onFbLinkButtonChange: (val: string) => change({ fbLinkButton: val }),
    onFbInstagramImageChange: (val: string) =>
      change({
        fbInstagramImageStoreId: val,
        fbInstagramImageErrors: [],
        fbInstagramImageCrop: null
      }),
    onFbStoriesImageChange: (val: string) =>
      change({ fbStoriesImageStoreId: val, fbStoriesImageErrors: [], fbStoriesImageCrop: null }),
    onGlImageCropChange: (val: Crop) => change({ glImageCrop: val }),
    onImageStoreIdChange: (imageStoreIds: Array<string>) => {
      const props = getCurrentProps(ownProps)
      _.each(imageStoreIds, (imageStoreId, imageStoreIdIndex) => {
        const {
          replicas,
          logoImageStoreId,
          snippetImageStoreId,
          vkBigImageStoreId,
          tmSmallImageStoreId,
          fbInstagramImageStoreId,
          fbStoriesImageStoreId
        } = props
        if (imageStoreIdIndex === 0) {
          change({
            multiImageStoreId: imageStoreId,
            multiImageErrors: [],
            vkMultiCrop: null,
            vkArticleCrop: null,
            vkSiteCrop: null,
            tmMultiCrop: null,
            fbImageCrop: null,
            glImageCrop: null
          })
          if (!logoImageStoreId) change({ logoImageCrop: null })
          if (!snippetImageStoreId) {
            change({ snippetImageCrop: null })
            _.each(replicas, (replica, index) => {
              changeReplica(index, { snippetImageErrors: [], snippetImageCrop: null })
            })
          }
          if (!vkBigImageStoreId) {
            change({ vkBigImageErrors: [] })
          }
          if (!tmSmallImageStoreId) {
            change({ tmSmallImageErrors: [], tmSmallImageCrop: null })
          }
          if (!fbInstagramImageStoreId) {
            change({ fbInstagramImageErrors: [], fbInstagramImageCrop: null })
          }
          if (!fbStoriesImageStoreId) {
            change({ fbStoriesImageErrors: [], fbStoriesImageCrop: null })
          }
        } else {
          addReplicaFunc(imageStoreId, vkBigImageStoreId)
        }
      })
    },
    onColumnImageChange: (imageStoreIds: Array<string>) => {
      change({
        columnImageStoreId: imageStoreIds[0],
        columnImageErrors: [],
        vkColumnCrop: null,
        tmColumnCrop: null
      })
    },
    onVkBigImageChange: (vkBigImageStoreIds: Array<string>) => {
      const props = getCurrentProps(ownProps)
      _.each(vkBigImageStoreIds, (vkBigImageStoreId, vkBigImageStoreIdIndex) => {
        if (vkBigImageStoreIdIndex === 0) {
          change({ vkBigImageStoreId: vkBigImageStoreId, vkBigImageErrors: [] })
        } else {
          addReplicaFunc(props.multiImageStoreId, vkBigImageStoreId)
        }
      })
    },
    onTmCarouselDescriptionChange: (description: string) =>
      change({ tmCarouselDescription: description, tmCarouselDescriptionErrors: [] })
  }
}

const AdEditContainer = connect<Props, OwnProps, _, _, _, _>(
  mapStateToProps,
  mapDispatchToProps
)(AdEdit)

export default AdEditContainer
